import { createFeatureSelector, createSelector } from '@ngrx/store';

import { RequestState, initialState as requestInitialState } from './reducers/request/request.reducers';
import {
  RequestFormState,
  initialState as requestFormInitialState,
} from './reducers/request-form/request-form.reducers';
import {
  SupplierListState,
  initialState as supplierListInitialState,
} from './reducers/supplier-list/supplier-list.reducers';
import {
  VerificationCacheState,
  initialState as verificationCacheInitialState,
} from './reducers/verification-cache/verification-cache.reducers';

export const REQUESTS_FEATURE_KEY = 'requests';

export interface RequestsFeatureState {
  list: RequestState;
  form: RequestFormState;
  supplierList: SupplierListState;
  verificationCache: VerificationCacheState;
}

export const selectRequestsFeatureState = createFeatureSelector<RequestsFeatureState>(REQUESTS_FEATURE_KEY);

export const selectRequestListState = createSelector(
  selectRequestsFeatureState,
  (s): RequestState => s?.list ?? requestInitialState
);

export const selectRequestFormState = createSelector(
  selectRequestsFeatureState,
  (s): RequestFormState => s?.form ?? requestFormInitialState
);

export const selectRequestItems = createSelector(selectRequestListState, (s) => s.items);
export const selectRequestsHasMore = createSelector(selectRequestListState, (s) => s.hasMore);
export const selectRequestsLoading = createSelector(selectRequestListState, (s) => s.loading);
export const selectRequestsError = createSelector(selectRequestListState, (s) => s.error ?? null);

export const selectRequestFormSaving = createSelector(selectRequestFormState, (s) => !!s.saving);
export const selectRequestFormSubmitting = createSelector(selectRequestFormState, (s) => !!s.submitting);
export const selectRequestFormError = createSelector(selectRequestFormState, (s) => s.error ?? null);
export const selectRequestFormLastSavedRequestId = createSelector(
  selectRequestFormState,
  (s) => s.lastSavedRequestId ?? null
);
export const selectRequestFormLastSubmittedRequestId = createSelector(
  selectRequestFormState,
  (s) => s.lastSubmittedRequestId ?? null
);

export const selectRequestFormLoadingRequest = createSelector(selectRequestFormState, (s) => !!s.loadingRequest);

export const selectRequestFormLoadedRequestId = createSelector(
  selectRequestFormState,
  (s) => s.loadedRequestId ?? null
);

export const selectRequestFormRequest = createSelector(selectRequestFormState, (s) => s.request ?? null);

export const selectSupplierListState = createSelector(
  selectRequestsFeatureState,
  (s): SupplierListState => s?.supplierList ?? supplierListInitialState
);

export const selectSupplierList = createSelector(selectSupplierListState, (s) => s.suppliers ?? []);
export const selectSupplierListTotal = createSelector(selectSupplierListState, (s) => s.totalElements ?? 0);
export const selectSupplierListLoading = createSelector(selectSupplierListState, (s) => !!s.loading);
export const selectSupplierListLoaded = createSelector(selectSupplierListState, (s) => !!s.loaded);
export const selectSupplierListError = createSelector(selectSupplierListState, (s) => s.error ?? null);
export const selectSupplierListLastPagination = createSelector(
  selectSupplierListState,
  (s) => s.lastPagination ?? null
);

export const selectVerificationCacheState = createSelector(
  selectRequestsFeatureState,
  (s): VerificationCacheState => s?.verificationCache ?? verificationCacheInitialState
);

export const selectVerificationCacheRequestId = createSelector(selectVerificationCacheState, (s) => s.requestId);

export const selectVerificationCacheEntries = createSelector(selectVerificationCacheState, (s) => s.entries);

export const selectVerificationCacheDetecting = createSelector(selectVerificationCacheState, (s) => s.detecting);

export const selectVerificationCacheEntry = (key: string) =>
  createSelector(selectVerificationCacheEntries, (entries) => entries[key] ?? null);
